import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import AddressLink from '@repo/ui/address-link'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { EyeOpenIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getAffectStaffDispatchPlaces } from '../../../../../api/affected-staffs/get-affected-staff-trip-bookings'
import { Paths } from '../../../../../lib/constants/path'
import useAffectedStaff from '../../../../../lib/hooks/use-affected-staff'
import { useNativeCountryName } from '../../../../../lib/hooks/use-native-country-name'
import useTranslate from '../../../../../lib/hooks/use-translate'
import { TripStepTypeEnum } from '../../../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { TripProperties } from '../../../../../lib/types/models/trip/properties.trip'
import { TripBasic } from '../../../../../lib/types/models/trip/trip'
import TransportPicto from '../../../../elements/transport-picto'
import LabelWithText from '../../../ols/label-with-text'

export type Props = {
  trip?: TripBasic
}

const formatTime = (time: { hours: number; minutes: number }) =>
  DateFormatter.formatTimeString(time.hours, time.minutes) ?? ''

const StaffPersonalSpaceTripSection = ({ trip }: Props) => {
  const nativeCountryName = useNativeCountryName()
  const { t } = useTranslate()
  const { affectedStaff } = useAffectedStaff()
  const tripLabel = TripProperties.getTripLabel(trip)
  const { navigate } = useNavigator()

  if (!trip) return null

  const { data: dispatchPlaces } = useQuery({
    queryKey: ['affected-staff-dispatch-places', affectedStaff?.id],
    queryFn: () =>
      getAffectStaffDispatchPlaces(affectedStaff!.id, { filter: { dispatched: true } }),
    enabled: !!affectedStaff?.id && !!affectedStaff?.travelInformationDiffused
  })

  const allerTrip = dispatchPlaces?.find(
    (place) => place.directionality === TripStepTypeEnum.INBOUND
  )
  const retourTrip = dispatchPlaces?.find(
    (place) => place.directionality === TripStepTypeEnum.OUTBOUND
  )

  const allerDeparturePlace = allerTrip?.tripStep?.departurePlace
  const retourDeparturePlace = retourTrip?.tripStep?.arrivalPlace
  const allerStaffAppointmentTime = allerTrip?.tripBooking?.isTakeInCharge
    ? allerTrip.tripBooking.meetingTimeForStaff
    : allerTrip?.tripStep?.timeBeforeDepartureForStaff
  const retourArrivalTime = retourTrip?.tripStep?.arrivalTime

  return (
    <InnerBox className="rounded-lg p-3 bg-white-hover">
      {/* header */}
      <div className="flex items-center justify-between w-full mobile:flex-col mobile:items-start mobile:gap-2">
        <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
          {t('staff.personal.space.my.journeys.details.tab.home.trip')}
        </Typography>
        <Button
          variant="ghost"
          icon={EyeOpenIcon}
          className="mobile:w-full"
          label={t('staff.personal.space.my.journeys.details.tab.home.view.trip.data')}
          onClick={() => {
            if (!affectedStaff) return
            navigate(
              Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS.replace(
                ':id',
                affectedStaff.id.toString()
              ).replace('*', Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_TRIP_INFORMATION)
            )
          }}
        />
      </div>

      <div className="h-2" />
      <div className="flex items-center gap-2">
        {trip.transportMeans?.picto && <TransportPicto picto={trip.transportMeans.picto} />}
        <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
          {tripLabel}
        </Typography>
      </div>
      <div className="h-2" />

      {!affectedStaff?.travelInformationDiffused || (!allerTrip && !retourTrip) ? (
        <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
          {t('staff.personal.space.travel.schedule.schedules.not.available.yet')}
        </Typography>
      ) : (
        <div className="grid grid-cols-2 gap-3.5 mobile:grid-cols-1">
          {/* Aller */}
          <div className="flex flex-col gap-2 ">
            <Badge
              color={tailwindColors.orange.light.DEFAULT}
              textColor={tailwindColors.orange.DEFAULT}
              text={t('staff.personal.space.details.trip.card.aller')}
            />
            <Box className="bg-primary-light rounded flex flex-col gap-2">
              <LabelWithText
                label={t('staff.personal.space.details.trip.card.date.of.departure')}
                data={
                  <Badge
                    variant="warning"
                    text={valueRender(allerTrip?.tripStep?.departureDate, (date) =>
                      DateFormatter.DDMMYYYY(date)
                    )}
                  />
                }
              />

              <LabelWithText
                label={t('staff.personal.space.details.trip.card.meeting.place')}
                data={
                  allerDeparturePlace ? (
                    <AddressLink
                      name={allerDeparturePlace.label}
                      address1={allerDeparturePlace.address1}
                      city={allerDeparturePlace.city}
                      zipCode={allerDeparturePlace.zipCode}
                      country={nativeCountryName(allerDeparturePlace.country)}
                    />
                  ) : (
                    '-'
                  )
                }
              />

              <LabelWithText
                label={t('staff.personal.space.details.trip.card.appointment.time')}
                data={
                  <Badge
                    variant="warning"
                    text={valueRender(allerStaffAppointmentTime, formatTime)}
                  />
                }
              />
            </Box>
          </div>

          {/* Retour */}
          <div className="flex flex-col gap-2 ">
            <Badge variant="success" text={t('staff.personal.space.details.trip.card.retour')} />
            <Box className="bg-primary-light rounded flex flex-col gap-2">
              <LabelWithText
                label={t('staff.personal.space.details.trip.card.date.of.return')}
                data={
                  <Badge
                    variant="warning"
                    text={valueRender(retourTrip?.tripStep?.departureDate, (date) =>
                      DateFormatter.DDMMYYYY(date)
                    )}
                  />
                }
              />

              <LabelWithText
                label={t('staff.personal.space.details.trip.card.place.of.arrival')}
                data={
                  retourDeparturePlace ? (
                    <AddressLink
                      name={retourDeparturePlace.label}
                      address1={retourDeparturePlace.address1}
                      city={retourDeparturePlace.city}
                      zipCode={retourDeparturePlace.zipCode}
                      country={nativeCountryName(retourDeparturePlace.country)}
                    />
                  ) : (
                    '-'
                  )
                }
              />

              <LabelWithText
                label={t('staff.personal.space.details.trip.card.time.of.arrival')}
                data={
                  <Badge variant="warning" text={valueRender(retourArrivalTime, formatTime)} />
                }
              />
            </Box>
          </div>
        </div>
      )}
    </InnerBox>
  )
}

export default StaffPersonalSpaceTripSection
